import React, { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isLinkNode } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';

import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { DropdownButton } from '../../../../DropdownButton';
import { ToolbarButton } from '../common';
import { getSelectedNode } from '../../utils';
import {
  $createCodeBlockNode,
  $createReferenceNode,
  $createMentionNode,
} from '../../nodes';

import { LinkDialog, QuoteDialog, TableDialog } from './components';
import { useSymbols } from './hooks';

interface LinkAndTablePluginProps {
  link: boolean;
  table: boolean;
  reference: boolean;
  codeBlock: boolean;
  quote: boolean;
  symbols: boolean;
  mention: boolean;
}

export const LinkAndTablePlugin = ({
  link,
  table,
  reference,
  codeBlock,
  quote,
  symbols: symbolsNode,
  mention,
}: LinkAndTablePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [selectionText, setSelectionText] = useState('');
  const [selectionUrl, setSelectionUrl] = useState('');
  const [isLinkNodeActive, setIsLinkNodeActive] = useState(false);
  const [isReferenceActive, setIsReferenceActive] = useState(false);
  const [isCodeBlockActive, setIsCodeBlockActive] = useState(false);
  const [isQuoteNodeActive, setIsQuoteNodeActive] = useState(false);

  const symbols = useSymbols(editor);

  const updateToolbar = useCallback(() => {
    editor.read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const textContent = selection.getTextContent();
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        let url = '';
        if (textContent) {
          if ($isLinkNode(node)) {
            url = node.exportJSON().url;
          }

          if ($isLinkNode(parent)) {
            url = parent.exportJSON().url;
          }

          if (url) {
            setSelectionUrl(() => url);
          }

          setSelectionText(() => textContent);
        } else {
          setSelectionText(() => '');
          setSelectionUrl(() => '');
        }

        if (
          node.getType() === 'quote-node' ||
          parent?.getType() === 'quote-node'
        ) {
          setIsQuoteNodeActive(() => true);
        } else {
          setIsQuoteNodeActive(() => false);
        }

        if (node.getType() === 'link' || parent?.getType() === 'link') {
          setIsLinkNodeActive(() => true);
        } else {
          setIsLinkNodeActive(() => false);
        }

        if (
          node.getType() === 'reference-node' ||
          parent?.getType() === 'reference-node'
        ) {
          setIsReferenceActive(() => true);
        } else {
          setIsReferenceActive(() => false);
        }

        if (
          node.getType() === 'code-block-node' ||
          parent?.getType() === 'code-block-node'
        ) {
          setIsCodeBlockActive(() => true);
        } else {
          setIsCodeBlockActive(() => false);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
    );
  }, [editor, updateToolbar]);

  const handleLinkClick = () => {
    setIsLinkDialogOpen(() => true);
  };

  const handleLinkDialogClose = () => {
    setIsLinkDialogOpen(() => false);
    setSelectionText(() => '');
    setSelectionUrl(() => '');
  };

  const handleTableClick = () => {
    setIsTableDialogOpen(() => true);
  };

  const handleTableDialogClose = () => {
    setIsTableDialogOpen(() => false);
  };

  const handleReferenceClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        if (
          node.getType() === 'reference-node' ||
          parent?.getType() === 'reference-node'
        ) {
          $setBlocksType(selection, () => $createParagraphNode());

          return;
        }

        if (node.getType() === 'root') {
          $insertNodes([$createReferenceNode()]);

          return;
        }

        $setBlocksType(selection, () => $createReferenceNode());
      }
    });
  };

  const handleBlockCodeClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        if (
          node.getType() === 'code-block-node' ||
          parent?.getType() === 'code-block-node'
        ) {
          $setBlocksType(selection, () => $createParagraphNode());

          return;
        }

        if (node.getType() === 'root') {
          $insertNodes([$createCodeBlockNode()]);

          return;
        }

        $setBlocksType(selection, () => $createCodeBlockNode());
      }
    });
  };

  const handleQuoteClick = () => {
    setIsQuoteDialogOpen(() => true);
  };

  const handleQuoteDialogClose = () => {
    setIsQuoteDialogOpen(() => false);
    setSelectionText(() => '');
  };

  const handleMentionClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const mentionNode = $createMentionNode();

        $insertNodes([mentionNode]);
      }
    });
  };

  return (
    <Box display="flex" alignItems="center" gap="2">
      {link && (
        <ToolbarButton onClick={handleLinkClick} tooltip="Вставить ссылку">
          <Icon
            name="chain_outline"
            size="16"
            color={
              isLinkNodeActive ? 'original.brand.primary' : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {table && (
        <ToolbarButton onClick={handleTableClick} tooltip="Вставить таблицу">
          <Icon
            name="table_header_outline"
            size="16"
            color="text.light.primary"
          />
        </ToolbarButton>
      )}

      {reference && (
        <ToolbarButton
          onClick={handleReferenceClick}
          tooltip="Вставить блок справки"
        >
          <Icon
            name="vkhrtek_reference"
            color={
              isReferenceActive
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
            size="16"
          />
        </ToolbarButton>
      )}

      {codeBlock && (
        <ToolbarButton
          onClick={handleBlockCodeClick}
          tooltip="Вставить блок кода"
        >
          <Icon
            name="vkhrtek_code_block"
            size="16"
            color={
              isCodeBlockActive
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {quote && (
        <ToolbarButton onClick={handleQuoteClick} tooltip="Вставить цитату">
          <Icon
            name="quote_closing"
            size="16"
            color={
              isQuoteNodeActive
                ? 'original.brand.primary'
                : 'text.light.primary'
            }
          />
        </ToolbarButton>
      )}

      {symbolsNode && (
        <DropdownButton
          menuItems={symbols}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Вставить спецсимвол">
              <Icon name="vkhrtek_omega" size="16" color="text.light.primary" />
            </ToolbarButton>
          )}
        />
      )}

      {mention && (
        <ToolbarButton
          onClick={handleMentionClick}
          tooltip="Упоминание пользователя"
        >
          <Icon name="vkhrtek_at" size="16" color="text.light.primary" />
        </ToolbarButton>
      )}

      {isLinkDialogOpen && (
        <LinkDialog
          editor={editor}
          open={isLinkDialogOpen}
          onClose={handleLinkDialogClose}
          selectionText={selectionText}
          selectionUrl={selectionUrl}
        />
      )}

      {isTableDialogOpen && (
        <TableDialog
          editor={editor}
          open={isTableDialogOpen}
          onClose={handleTableDialogClose}
        />
      )}

      {isQuoteDialogOpen && (
        <QuoteDialog
          editor={editor}
          open={isQuoteDialogOpen}
          onClose={handleQuoteDialogClose}
          selectionText={selectionText}
        />
      )}
    </Box>
  );
};
